import { Button } from "@/components/ui/button";
import SEO from "@/components/seo";
import {
  ArrowUpRight,
  Heart,
  MapPin,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import logoPath from "@assets/logo.webp";
import teamPhoto from "@assets/team/isabel-milbourn.jpg";

const accountUrl =
  "https://www.timetopet.com/portal/tallylassiepets/create-account";

const trustPoints = [
  {
    icon: MapPin,
    title: "Local & woman-owned",
    detail: "A Tallahassee pet-care team rooted in the community.",
  },
  {
    icon: ShieldCheck,
    title: "Reliable solo walks",
    detail: "A 100% reliability guarantee for solo dog walking.",
  },
  {
    icon: Heart,
    title: "Trained team",
    detail: "Our team receives formal training and orientation.",
  },
];

export default function NewClientOffer() {
  return (
    <main className="min-h-[100dvh] overflow-hidden bg-[#fbf8f1] text-[#29263b]">
      <SEO
        title="Two Free Dog Walks for New Clients"
        description="New clients booking walks get their first two walks free. Meet Tallahassee Lassie, a local, woman-owned pet-care team."
        path="/new"
      />

      <div className="relative isolate">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 -top-40 -z-10 h-[34rem] w-[34rem] rounded-full bg-[#eee8dc]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-48 top-[30rem] -z-10 h-[30rem] w-[30rem] rounded-full bg-[#f1e9d8]/70"
        />

        <header className="mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-5 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3">
            <img
              src={logoPath}
              alt="Tallahassee Lassie Pet Care"
              className="h-12 w-auto object-contain sm:h-14"
              width={700}
              height={547}
            />
            <span className="hidden font-serif text-lg font-bold tracking-tight text-primary sm:block">
              Tallahassee Lassie
            </span>
          </div>
          <span className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#696579] sm:flex">
            <span className="h-2 w-2 rounded-full bg-[#7b8b68]" />
            Tallahassee, Florida
          </span>
        </header>

        <section className="mx-auto grid w-full max-w-7xl items-center gap-12 px-5 pb-16 pt-8 sm:px-8 md:pb-24 md:pt-12 lg:grid-cols-[1.02fr_0.98fr] lg:gap-16 lg:px-12 lg:pt-14">
          <div className="relative z-10 max-w-2xl animate-[offer-rise_.7s_ease-out_both]">
            <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#e6dece] bg-[#fffdf8]/80 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.19em] text-[#746d5d] shadow-sm">
              <Sparkles size={14} className="text-[#a97842]" />
              A little welcome from our team
            </p>
            <h1 className="font-serif text-[clamp(3.25rem,8vw,6.6rem)] font-bold leading-[0.94] tracking-[-0.055em] text-[#302b44]">
              Good walks.
              <br />
              <span className="relative inline-block text-primary">
                Good neighbors.
                <span
                  aria-hidden="true"
                  className="absolute -bottom-1 left-1 h-2 w-[82%] -rotate-2 rounded-full bg-[#d6bd91]/70"
                />
              </span>
            </h1>
            <div
              className="mt-7 max-w-xl rounded-tl-2xl rounded-br-2xl rounded-tr-sm rounded-bl-sm border border-[#d6bd91] bg-[#f1e7d4] px-5 py-4 shadow-sm sm:px-6 sm:py-5"
              data-testid="offer-highlight"
            >
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#746d5d] sm:text-sm">
                For new clients booking walks
              </p>
              <p className="mt-1 font-serif text-2xl font-bold leading-tight text-[#302b44] sm:text-3xl">
                Your first two walks are free.
              </p>
            </div>
            <p className="mt-4 max-w-lg text-base leading-7 text-[#777385]">
              Thoughtful dog walking from a local team, with the care and
              communication you want while you’re away.
            </p>

            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="group h-auto min-h-14 w-full justify-between gap-5 rounded-tl-2xl rounded-br-2xl rounded-tr-sm rounded-bl-sm bg-primary px-6 py-4 text-base font-semibold text-primary-foreground shadow-[0_12px_28px_-14px_rgba(72,55,119,0.6)] transition duration-200 hover:-translate-y-0.5 hover:bg-primary/90 sm:w-auto sm:min-w-[19rem]"
              >
                <a
                  href={accountUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link-create-account"
                >
                  Start with a meet & greet
                  <ArrowUpRight
                    size={19}
                    className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </Button>
            </div>

          </div>

          <div className="relative mx-auto w-full max-w-[35rem] animate-[offer-rise_.8s_ease-out_.12s_both] lg:ml-auto">
            <div
              aria-hidden="true"
              className="absolute -right-3 top-8 h-[82%] w-[78%] rounded-tl-[9rem] rounded-br-[9rem] rounded-tr-2xl rounded-bl-2xl bg-[#e4d9c2] sm:-right-5"
            />
            <figure className="relative z-10 overflow-hidden rounded-tl-[9rem] rounded-br-[9rem] rounded-tr-2xl rounded-bl-2xl border-[7px] border-[#fffdf8] shadow-[0_24px_60px_-28px_rgba(48,43,68,0.4)]">
              <img
                src={teamPhoto}
                alt="A Tallahassee Lassie team member in a purple shirt walking a dog on a tree-lined path."
                className="aspect-[4/5] w-full object-cover object-center"
                width={675}
                height={1200}
                fetchPriority="high"
              />
            </figure>
            <div className="absolute -bottom-5 left-0 z-20 max-w-[17rem] rounded-tl-2xl rounded-br-2xl rounded-tr-sm rounded-bl-sm border border-[#e8e0d2] bg-[#fffdf8] px-5 py-4 shadow-[0_12px_30px_-20px_rgba(48,43,68,0.45)] sm:-left-8">
              <p className="font-serif text-lg font-bold text-[#39334c]">
                Care that feels close to home.
              </p>
              <p className="mt-1 text-sm text-[#777385]">
                From your Tallahassee neighbors.
              </p>
            </div>
          </div>
        </section>
      </div>

      <section className="border-y border-[#e9e2d6] bg-[#f5f0e7]">
        <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8 md:py-16 lg:px-12">
          <div className="mb-8 max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b7554]">
              Good care, built on trust
            </p>
            <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-[#302b44] sm:text-4xl">
              A steady hand on the leash.
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3 md:gap-6">
            {trustPoints.map((point) => (
              <article
                key={point.title}
                className="flex gap-4 border-t border-[#d9d0c0] py-5 md:py-6"
                data-testid={`trust-${point.title.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-tl-xl rounded-br-xl rounded-tr-sm rounded-bl-sm bg-[#e7dece] text-primary">
                  <point.icon size={21} strokeWidth={1.8} />
                </span>
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#39334c]">
                    {point.title}
                  </h3>
                  <p className="mt-2 max-w-sm text-sm leading-6 text-[#716c7a]">
                    {point.detail}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto flex w-full max-w-7xl flex-col items-start gap-6 px-5 py-14 sm:px-8 md:flex-row md:items-center md:justify-between md:py-16 lg:px-12">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b7554]">
            Start with a hello
          </p>
          <h2 className="mt-3 max-w-2xl font-serif text-3xl font-bold leading-tight tracking-tight text-[#302b44] sm:text-4xl">
            Let’s get to know you and your dog.
          </h2>
        </div>
        <Button
          asChild
          size="lg"
          className="group h-14 w-full gap-3 rounded-tl-2xl rounded-br-2xl rounded-tr-sm rounded-bl-sm bg-[#d9c9a8] px-6 text-base font-semibold text-[#302b44] shadow-sm transition duration-200 hover:-translate-y-0.5 hover:bg-[#d1bd97] md:w-auto"
        >
          <a
            href={accountUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-meet-and-greet"
          >
            Create your account
            <ArrowUpRight
              size={18}
              className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </Button>
      </section>

      <style>{`
        @keyframes offer-rise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </main>
  );
}
